import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, Card, Button, ActivityIndicator, DataTable, Searchbar, Menu } from 'react-native-paper';
import PeriodoChip from '../../components/PeriodoChip';
import { Picker } from '@react-native-picker/picker';
import { Ionicons } from '@expo/vector-icons';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import { exportService } from '../../services/exportService';

const PERIODOS = [
  { id: 'hoy', label: 'Hoy' }, { id: 'esta_semana', label: 'Esta Semana' },
  { id: 'este_mes', label: 'Este Mes' }, { id: 'ultimo_trimestre', label: 'Último Trimestre' },
  { id: 'este_ano', label: 'Este Año' },
];

const ReporteProductosVendedorScreen = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('este_mes');
  const [vendedores, setVendedores] = useState([]);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState(null);
  const [page, setPage] = useState(0);
  const itemsPerPage = 25;

  useEffect(() => { cargarVendedores(); cargarReporte(); }, []);
  useEffect(() => { filtrarDatos(); }, [searchQuery, data]);
  useEffect(() => { setPage(0); }, [filteredData.length, periodoActivo, vendedorSeleccionado]);

  const cargarVendedores = async () => {
    try {
      const usuarios = await api.getUsuarios();
      setVendedores((usuarios || []).filter(u => u.activo !== 0));
    } catch (e) { console.error(e); }
  };

  const cargarReporte = async (periodo = periodoActivo, vendId = vendedorSeleccionado) => {
    try {
      setLoading(true);
      let url = `/reportes/productos-por-vendedor?periodo=${periodo}`;
      if (vendId) url += `&vendedor_id=${vendId}`;
      const response = await api.get(url);
      setData(response);
      setFilteredData(response.productos || []);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el reporte');
    } finally { setLoading(false); }
  };

  const cambiarPeriodo = (p) => { setPeriodoActivo(p); cargarReporte(p, vendedorSeleccionado); };
  const cambiarVendedor = (v) => { setVendedorSeleccionado(v); cargarReporte(periodoActivo, v); };

  const filtrarDatos = () => {
    if (!data?.productos) { setFilteredData([]); return; }
    if (!searchQuery.trim()) { setFilteredData(data.productos); return; }
    const q = searchQuery.toLowerCase();
    setFilteredData(data.productos.filter(p =>
      (p.producto_nombre || '').toLowerCase().includes(q) ||
      (p.vendedor_nombre || '').toLowerCase().includes(q) ||
      (p.producto_codigo || '').toLowerCase().includes(q)
    ));
  };

  const exportarDatos = async (formato) => {
    setMenuVisible(false);
    if (!filteredData.length) return Alert.alert('Error', 'No hay datos');
    const headers = ['Vendedor', 'Código', 'Producto', 'Categoría', 'Cantidad', 'Total Vendido', 'Veces', 'Precio Prom.'];
    const exportData = filteredData.map(p => ({
      'Vendedor': p.vendedor_nombre, 'Código': p.producto_codigo, 'Producto': p.producto_nombre,
      'Categoría': p.categoria, 'Cantidad': p.cantidad_vendida,
      'Total Vendido': parseFloat(p.total_vendido || 0).toFixed(2),
      'Veces': p.veces_vendido, 'Precio Prom.': parseFloat(p.precio_promedio || 0).toFixed(2),
    }));
    await exportService.exportData(formato, exportData, 'productos_por_vendedor', headers, 'Reporte de Productos por Vendedor');
  };

  const resumen = data?.resumen_vendedores || [];

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Productos por Vendedor</Text>
          <Text style={styles.subtitle}>Qué productos vende cada vendedor</Text>
        </View>
        <Menu visible={menuVisible} onDismiss={() => setMenuVisible(false)}
          anchor={<Button icon="download" mode="outlined" onPress={() => setMenuVisible(true)} disabled={loading}>Exportar</Button>}>
          <Menu.Item title="Excel" onPress={() => exportarDatos('excel')} />
          <Menu.Item title="PDF" onPress={() => exportarDatos('pdf')} />
          <Menu.Item title="CSV" onPress={() => exportarDatos('csv')} />
        </Menu>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtrosContainer} contentContainerStyle={styles.filtrosContent}>
        <View style={styles.filtros}>
          {PERIODOS.map(p => (
            <PeriodoChip
              key={p.id}
              active={periodoActivo === p.id}
              onPress={() => !loading && cambiarPeriodo(p.id)}
            >
              {p.label}
            </PeriodoChip>
          ))}
        </View>
      </ScrollView>

      {loading ? (
        <View style={styles.loader}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={{ marginTop: 10, color: '#666' }}>Generando reporte...</Text>
        </View>
      ) : (
      <ScrollView>
        {/* Filtro por vendedor */}
        <View style={styles.pickerSection}>
          <Text style={styles.pickerLabel}>Filtrar por vendedor:</Text>
          <View style={styles.pickerContainer}>
            <Picker selectedValue={vendedorSeleccionado} onValueChange={cambiarVendedor} style={{ height: 50 }}>
              <Picker.Item label="Todos los vendedores" value={null} />
              {vendedores.map(v => (
                <Picker.Item key={v.id} label={`${v.nombre} (${v.rol})`} value={v.id} />
              ))}
            </Picker>
          </View>
        </View>

        {/* Resumen por vendedor */}
        {resumen.length > 0 && (
          <Card style={styles.card}>
            <Card.Content>
              <Text style={styles.cardTitle}>Resumen por Vendedor</Text>
              {resumen.map((r, i) => (
                <View key={i} style={styles.vendedorRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.vendedorNombre}>{r.vendedor_nombre}</Text>
                    <Text style={styles.vendedorDetalle}>
                      {r.total_productos_distintos} productos | {r.total_unidades} unidades
                    </Text>
                  </View>
                  <Text style={styles.vendedorMonto}>{formatCurrency(r.total_vendido)}</Text>
                </View>
              ))}
            </Card.Content>
          </Card>
        )}

        <Searchbar placeholder="Buscar producto o vendedor..." onChangeText={setSearchQuery} value={searchQuery} style={styles.searchbar} />

        <ScrollView horizontal style={{ paddingHorizontal: 16 }}>
          <DataTable style={{ backgroundColor: '#fff', minWidth: 900 }}>
            <DataTable.Header>
              <DataTable.Title style={{ minWidth: 120 }}>Vendedor</DataTable.Title>
              <DataTable.Title style={{ minWidth: 60 }}>Código</DataTable.Title>
              <DataTable.Title style={{ minWidth: 150 }}>Producto</DataTable.Title>
              <DataTable.Title style={{ minWidth: 100 }}>Categoría</DataTable.Title>
              <DataTable.Title numeric style={{ minWidth: 70 }}>Cant.</DataTable.Title>
              <DataTable.Title numeric style={{ minWidth: 100 }}>Total</DataTable.Title>
              <DataTable.Title numeric style={{ minWidth: 60 }}>Veces</DataTable.Title>
              <DataTable.Title numeric style={{ minWidth: 90 }}>Precio Prom.</DataTable.Title>
            </DataTable.Header>
            {filteredData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((p, idx) => (
              <DataTable.Row key={page * itemsPerPage + idx}>
                <DataTable.Cell style={{ minWidth: 120 }}>{p.vendedor_nombre}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 60 }}>{p.producto_codigo}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 150 }}>{p.producto_nombre}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 100 }}>{p.categoria}</DataTable.Cell>
                <DataTable.Cell numeric style={{ minWidth: 70 }}>{p.cantidad_vendida}</DataTable.Cell>
                <DataTable.Cell numeric style={{ minWidth: 100 }}>{formatCurrency(p.total_vendido)}</DataTable.Cell>
                <DataTable.Cell numeric style={{ minWidth: 60 }}>{p.veces_vendido}</DataTable.Cell>
                <DataTable.Cell numeric style={{ minWidth: 90 }}>{formatCurrency(p.precio_promedio)}</DataTable.Cell>
              </DataTable.Row>
            ))}
            {filteredData.length > itemsPerPage && (
              <DataTable.Pagination
                page={page}
                numberOfPages={Math.ceil(filteredData.length / itemsPerPage)}
                onPageChange={setPage}
                label={`${page * itemsPerPage + 1}-${Math.min((page + 1) * itemsPerPage, filteredData.length)} de ${filteredData.length}`}
                showFastPaginationControls
                numberOfItemsPerPage={itemsPerPage}
              />
            )}
          </DataTable>
        </ScrollView>

        {filteredData.length === 0 && (
          <View style={styles.empty}><Ionicons name="cube-outline" size={50} color="#ccc" /><Text style={{ color: '#999', marginTop: 10 }}>Sin datos</Text></View>
        )}
        <View style={{ height: 40 }} />
      </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 16, backgroundColor: '#fff', elevation: 2 },
  title: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  subtitle: { fontSize: 14, color: '#666', marginTop: 2 },
  filtrosContainer: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee', height: 56, flexGrow: 0, flexShrink: 0 },
  filtrosContent: { alignItems: 'center', paddingVertical: 10 },
  filtros: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16 },
  chip: { marginRight: 8, backgroundColor: '#f0f0f0' },
  chipActivo: { backgroundColor: '#4CAF50' },
  chipTexto: { color: '#333' },
  chipTextoActivo: { color: '#fff', fontWeight: '600' },
  pickerSection: { margin: 16, marginBottom: 8 },
  pickerLabel: { fontSize: 13, color: '#666', marginBottom: 4, fontWeight: '500' },
  pickerContainer: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, backgroundColor: '#fff', overflow: 'hidden' },
  card: { margin: 16, marginBottom: 8, elevation: 2 },
  cardTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 10 },
  vendedorRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  vendedorNombre: { fontSize: 14, fontWeight: '600', color: '#333' },
  vendedorDetalle: { fontSize: 12, color: '#888' },
  vendedorMonto: { fontSize: 15, fontWeight: '700', color: '#0066cc' },
  searchbar: { margin: 16, marginBottom: 8 },
  empty: { alignItems: 'center', padding: 40 },
});

export default ReporteProductosVendedorScreen;
